import {
  CARD_ART_CROP,
  EXCLUSIVE_PORTRAIT_PATH,
  LAST_CALL_PORTRAIT_ID,
  isExclusiveAvatarCard,
  parseAvatarConfig,
  type AvatarConfig,
} from '@shared/index'
import { apiUrl } from '../api/http'
import { getLocalCardImageUrl } from '../utils/cardImages'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import {
  IMAGE_FRAME_PORTRAIT,
  avatarArtInset,
  drawCircleFrame,
  drawSquareFrame,
  isImageFrame,
  loadImageFrame,
} from './drawFrame'

export type AvatarCrop = 'full' | 'bust'

const imgCache = new Map<string, Promise<HTMLImageElement>>()
const canvasCache = new Map<string, string>()

function loadImage(src: string): Promise<HTMLImageElement> {
  const hit = imgCache.get(src)
  if (hit !== undefined) return hit
  const img = new Image()
  img.decoding = 'async'
  const pending = new Promise<HTMLImageElement>((resolve, reject) => {
    img.onload = () => resolve(img)
    img.onerror = () => {
      if (imgCache.get(src) === pending) imgCache.delete(src)
      reject(new Error(`card art missing: ${src}`))
    }
  })
  imgCache.set(src, pending)
  img.src = src
  return pending
}

function cacheKey(
  cfg: AvatarConfig,
  crop: AvatarCrop,
  width: number,
  height: number,
  circle: boolean,
): string {
  const region = isExclusiveAvatarCard(cfg.cardId)
    ? 'exclusive'
    : useDuelPrefsStore().cardArtRegion
  return (
    `card2|${cfg.cardId}|${cfg.rev ?? 0}|${region}|${cfg.frameId}|` +
    `${crop}|${width}x${height}|${circle ? 'o' : 's'}`
  )
}

const EXCLUSIVE_PORTRAIT_SRC = '/assets/avatars/exclusive/crx.png'

/** Uploaded portrait when `rev` is set; otherwise the built-in bust. */
function exclusivePortraitSrc(rev: number | undefined): string {
  if (rev === undefined) return EXCLUSIVE_PORTRAIT_SRC
  return apiUrl(`${EXCLUSIVE_PORTRAIT_PATH}?v=${rev}`)
}

async function loadExclusivePortrait(rev: number | undefined): Promise<HTMLImageElement> {
  if (rev === undefined) return loadImage(EXCLUSIVE_PORTRAIT_SRC)
  try {
    return await loadImage(exclusivePortraitSrc(rev))
  } catch {
    return loadImage(EXCLUSIVE_PORTRAIT_SRC)
  }
}

function artBox(img: HTMLImageElement): {
  sx: number
  sy: number
  sw: number
  sh: number
} {
  return {
    sx: img.naturalWidth * CARD_ART_CROP.x,
    sy: img.naturalHeight * CARD_ART_CROP.y,
    sw: img.naturalWidth * CARD_ART_CROP.w,
    sh: img.naturalHeight * CARD_ART_CROP.h,
  }
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  const side = Math.min(sw, sh)
  ctx.drawImage(
    img,
    sx + (sw - side) / 2,
    sy + (sh - side) / 2,
    side,
    side,
    dx,
    dy,
    dw,
    dh,
  )
}

/**
 * Square cover-crop of the illustration into the inner frame window.
 */
function drawCoverArt(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  const box = artBox(img)
  drawCover(ctx, img, box.sx, box.sy, box.sw, box.sh, dx, dy, dw, dh)
}

/** Portraits that are a whole image, not a card's artwork window. */
const SPECIAL_PORTRAIT_SRC = new Map<string, string>([
  [LAST_CALL_PORTRAIT_ID, '/assets/avatars/last-call-portrait.png?v=3'],
])

/** Cover the circular hole. The ring is painted afterwards and hides the rim. */
function drawCoverInCircle(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  size: number,
  radius: number,
): void {
  const diameter = radius * 2
  const scale = Math.max(diameter / sw, diameter / sh)
  const dw = sw * scale
  const dh = sh * scale
  ctx.save()
  ctx.beginPath()
  ctx.arc(size / 2, size / 2, radius, 0, Math.PI * 2)
  ctx.clip()
  ctx.drawImage(
    img,
    sx,
    sy,
    sw,
    sh,
    (size - dw) / 2,
    (size - dh) / 2,
    dw,
    dh,
  )
  ctx.restore()
}
function drawCoverFull(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  drawCover(ctx, img, 0, 0, img.naturalWidth, img.naturalHeight, dx, dy, dw, dh)
}

/**
 * Draw a square framed portrait. Bust/full both cover-crop into the inner
 * square so the frame always has a clean 1:1 photo.
 */
export async function composeAvatar(
  raw: AvatarConfig,
  opts?: {
    crop?: AvatarCrop
    width?: number
    height?: number
    circle?: boolean
  },
): Promise<HTMLCanvasElement> {
  const cfg = parseAvatarConfig(raw)
  const circle = opts?.circle === true
  const size = Math.max(1, Math.round(opts?.width ?? opts?.height ?? 256))
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas

  const inset = avatarArtInset(size, cfg.frameId)
  const inner = Math.max(1, size - inset * 2)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  const exclusive = isExclusiveAvatarCard(cfg.cardId)
  const special = SPECIAL_PORTRAIT_SRC.get(cfg.cardId)
  const wholeImage = exclusive || special !== undefined
  const paint = wholeImage ? drawCoverFull : drawCoverArt
  const loadPortrait = (): Promise<HTMLImageElement> =>
    special
      ? loadImage(special)
      : exclusive
        ? loadExclusivePortrait(cfg.rev)
        : loadImage(getLocalCardImageUrl(cfg.cardId))
  if (isImageFrame(cfg.frameId)) {
    try {
      await loadImageFrame(cfg.frameId)
    } catch {
      /* The ring is missing; the portrait still paints. */
    }
    try {
      const img = await loadPortrait()
      const box = wholeImage
        ? { sx: 0, sy: 0, sw: img.naturalWidth, sh: img.naturalHeight }
        : artBox(img)
      drawCoverInCircle(
        ctx,
        img,
        box.sx,
        box.sy,
        box.sw,
        box.sh,
        size,
        size * IMAGE_FRAME_PORTRAIT,
      )
    } catch {
      /* Missing scan: the ring still frames an empty window. */
    }
    drawSquareFrame(ctx, size, cfg.frameId)
    return canvas
  }
  try {
    const img = await loadPortrait()
    if (circle) {
      paint(ctx, img, 0, 0, size, size)
      ctx.globalCompositeOperation = 'destination-in'
      ctx.beginPath()
      ctx.arc(size / 2, size / 2, size / 2 - inset, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalCompositeOperation = 'source-over'
      drawCircleFrame(ctx, size, cfg.frameId)
    } else {
      paint(ctx, img, inset, inset, inner, inner)
      drawSquareFrame(ctx, size, cfg.frameId)
    }
  } catch {
    // Missing scan: still paint the frame so the slot is not empty.
    if (circle) drawCircleFrame(ctx, size, cfg.frameId)
    else drawSquareFrame(ctx, size, cfg.frameId)
  }

  return canvas
}

/**
 * Cached PNG data URL for Phaser textures and img tags.
 */
export async function composeAvatarDataUrl(
  raw: AvatarConfig,
  opts?: {
    crop?: AvatarCrop
    width?: number
    height?: number
    circle?: boolean
  },
): Promise<string> {
  const cfg = parseAvatarConfig(raw)
  const crop: AvatarCrop = opts?.crop ?? 'full'
  const circle = opts?.circle === true
  const size = Math.max(1, Math.round(opts?.width ?? opts?.height ?? 256))
  const key = cacheKey(cfg, crop, size, size, circle)
  const cached = canvasCache.get(key)
  if (cached) return cached
  const canvas = await composeAvatar(cfg, { crop, width: size, height: size, circle })
  const url = canvas.toDataURL('image/png')
  canvasCache.set(key, url)
  return url
}
